import { createRouter, createWebHistory } from 'vue-router'
import { resolveClient, resolveClientKeyByAccId } from '../config/clients'
import { login } from '../services/auth'
import { useAuthStore } from '../stores/auth'
import { loadLastClientKey, storeInvitedSupplier, useSessionStore } from '../stores/session'

/**
 * El primer segmento del path identifica al cliente (receptor): `/{client}/...`. Una
 * entrada por la raíz sigue al último cliente usado (y solo muestra "sitio no encontrado"
 * cuando no hay uno). `/callback` es el único redirect URI registrado en Keycloak; la ruta
 * original viaja en el `state` de OIDC y se restaura al volver del login.
 */
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/callback',
      name: 'callback',
      component: () => import('../views/CallbackView.vue'),
      meta: { public: true },
    },
    {
      // Sin cliente en la ruta: aterrizaje de invitación (Keycloak redirige aquí con
      // invited_acc / invited_country / invited_tax_id tras aceptar) o último cliente usado.
      path: '/',
      name: 'root',
      redirect: (to) => {
        const invitedAcc = to.query.invited_acc as string | undefined
        if (invitedAcc) {
          const clientKey = resolveClientKeyByAccId(invitedAcc)
          if (clientKey) {
            const country = to.query.invited_country as string | undefined
            const taxId = to.query.invited_tax_id as string | undefined
            if (country && taxId) {
              storeInvitedSupplier(invitedAcc, country, taxId)
            }
            // query vacia: los params del aterrizaje no deben quedar en la URL del home
            return { name: 'home', params: { client: clientKey }, query: {} }
          }
          console.warn(`Aterrizaje de invitación con accId desconocido en este ambiente: ${invitedAcc}`)
        }
        const lastClient = loadLastClientKey()
        return lastClient ? { name: 'home', params: { client: lastClient } } : { name: 'not-found' }
      },
    },
    {
      path: '/:client/select-supplier',
      name: 'select-supplier',
      component: () => import('../views/SelectSupplierView.vue'),
    },
    {
      path: '/:client',
      component: () => import('../views/AppLayout.vue'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('../views/HomeView.vue'),
        },
        {
          path: 'payments',
          name: 'payments',
          component: () => import('../views/PaymentsView.vue'),
        },
        {
          path: 'payments/history',
          name: 'payments-history',
          component: () => import('../views/PaymentsHistoryView.vue'),
        },
        {
          path: 'payments/history/:date',
          name: 'payment-detail',
          component: () => import('../views/PaymentDetailView.vue'),
        },
        {
          path: 'documents',
          name: 'documents',
          component: () => import('../views/DocumentsView.vue'),
        },
        {
          path: 'documents/:id',
          name: 'document-detail',
          component: () => import('../views/DocumentDetailView.vue'),
        },
        {
          // Gestion de usuarios del proveedor (la vista redirige al home si no es OWNER)
          path: 'users',
          name: 'users',
          component: () => import('../views/UsersView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { public: true },
    },
  ],
})

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const clientKey = to.params.client as string
  const clientConfig = resolveClient(clientKey)
  if (!clientConfig) {
    return { name: 'not-found' }
  }

  const session = useSessionStore()
  session.setClient(clientKey, clientConfig)

  const auth = useAuthStore()
  await auth.initialize()

  if (!auth.isAuthenticated) {
    await login(to.fullPath)
    return false
  }

  if (!session.selectedSupplier) {
    if (!session.suppliersLoaded) {
      try {
        await session.loadSuppliers()
      } catch (err) {
        console.error('Error al cargar los proveedores del usuario:', err)
      }
    }

    if (!session.selectedSupplier && to.name !== 'select-supplier') {
      return { name: 'select-supplier', params: { client: clientKey } }
    }
  }

  // El selector solo aplica cuando hay mas de un proveedor entre los que elegir; con uno
  // solo (auto-seleccionado) una entrada directa a select-supplier sigue al home.
  if (to.name === 'select-supplier' && session.selectedSupplier && session.suppliers.length <= 1) {
    return { name: 'home', params: { client: clientKey } }
  }

  return true
})

export default router
